import axios from 'axios'
import store from '@/store'
const dsBridge = require('dsbridge')
import * as path from 'path'

import {
  generateRandomString,
  cryptoJSAesEncrypt,
  cryptoJSAesDecrypt,
  rsaEncrypt,
  rsaDecrypt,
  toSign
} from '@/utils/cryptoJS'

import { getSecureRSAPublicKey } from '@/utils/secureAES'

import { sortJsonKeys } from '@/utils/object.js'
import { appendPlatformType } from '@/utils/platformType'
import i18n from '@/locales'
import { attachApiRequestMeta, logApiError, logApiRequest, logApiResponse } from '@/utils/apiLogger'

/**
 * 过滤对象中的 null 和 undefined
 */
function removeNullAndUndefined(obj) {
  if (obj == null) return undefined
  if (Array.isArray(obj)) {
    return obj.map(removeNullAndUndefined).filter(item => item !== undefined)
  }
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([key, value]) => [key, removeNullAndUndefined(value)])
        .filter(([, value]) => value !== undefined)
    )
  }
  return obj
}

/**
 * 处理 JSON 对象，转换所有值为字符串
 */
function stringifyObjectValues(input) {
  if (Array.isArray(input))
    return input.length === 1 ? stringifyObjectValues(input[0]) : input.map(stringifyObjectValues)
  if (typeof input === 'object' && input !== null)
    return Object.fromEntries(Object.entries(input).map(([k, v]) => [k, stringifyObjectValues(v)]))
  return String(input)
}

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 30000 // request timeout
})
// request interceptor
service.interceptors.request.use(
  config => {
    // do something before request is sent
    // if (store.getters.token) {
    // let each request carry token
    // ['X-Token'] is a custom headers key
    // please modify it according to the actual situation
    const aesKey = generateRandomString()
    const aesIV = generateRandomString()
    const token = store.state.user.token ? 'Bearer ' + store.state.user.token : 'Bearer ' + store.state.user.tokenJT
    const requestMethod = config.method && config.method.toLowerCase()
    let requestPayload

    config.headers['Authorization'] = token
    if (i18n.locale === 'en') {
      config.headers['lang'] = 'en-US'
    } else if (i18n.locale === 'zh') {
      config.headers['lang'] = 'zh-CN'
    } else {
      config.headers['lang'] = 'th-TH'
    }

    config.headers['app-name'] = process.env.VUE_APP_PLATFORM

    if (dsBridge.hasNativeMethod('jsapi.getVersionCode')) {
      config.headers['app-version'] = dsBridge.call('jsapi.getVersionCode', '')
    }

    // store.state.user.token ? config.headers['Authorization'] = 'Bearer ' + store.state.user.token : config.headers['Authorization'] = 'Bearer ' + store.state.user.tokenJT
    // config.headers['mock'] = 'abc ' //测四环境数据

    //加签名
    const timestamp = Date.now()
    let sortedJson
    let sign
    if (requestMethod === 'get') {
      config.params = appendPlatformType(config.data ? config.data : config.params)
      if (config.params || config.data) {
        config.data = config.params
        config.data.timestamp = timestamp
        config.data = {
          ...config.data
        }
        config.data = removeNullAndUndefined(config.data)
        sortedJson = stringifyObjectValues(sortJsonKeys(config.data))
        sign = toSign(JSON.stringify(sortedJson))
        requestPayload = config.data
      }
    } else if (requestMethod === 'post') {
      config.data = appendPlatformType(config.data)
      if (config.data) {
        config.data.timestamp = timestamp
        config.data = {
          ...config.data
        }
        config.data = removeNullAndUndefined(config.data)
        sortedJson = sortJsonKeys(config.data)
        sign = toSign(JSON.stringify(sortedJson))
        requestPayload = config.data
      }
    }
    config.headers.SIGN = sign
    const requestMeta = attachApiRequestMeta(config, requestPayload)
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'staging') {
      config.headers['route-version'] = process.env.VUE_APP_ROUTE_VERSION
    }
    //加密
    if (process.env.VUE_APP_ENCRYPT === 'true') {
      const authOb = {
        aesKey: aesKey,
        aesIv: aesIV,
        token: token
      }
      config.headers['Authorization'] = rsaEncrypt(JSON.stringify(authOb))
      let data = {}
      if (config.method && config.method.toLowerCase() === 'get') {
        let params = config.params || {}
        data.body = cryptoJSAesEncrypt(JSON.stringify(params), aesKey, aesIV)
        config.params = {}
        data.method = 'get'
      } else if (config.method && config.method.toLowerCase() === 'post') {
        let params = config.data || {}
        data.body = cryptoJSAesEncrypt(JSON.stringify(params), aesKey, aesIV)
        data.method = 'post'
      }
      // data.aesKey = rsaEncrypt(aesKey)
      // data.aesIv = rsaEncrypt(aesIV)
      data.timestamp = new Date().getTime()
      config.method = 'post'
      config.data = data
    }
    logApiRequest(requestMeta)
    return config
  },
  error => {
    // do something with request error
    logApiError(error)
    return Promise.reject(error)
  }
)
// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  response => {
    const res = response.data
    //解密
    if (process.env.VUE_APP_ENCRYPT === 'true') {
      const headers = response.headers
      let key = ''
      let iv = ''
      if (headers && headers.authorization) {
        const publicPEM = getSecureRSAPublicKey()
        const result = JSON.parse(rsaDecrypt(headers.authorization, publicPEM))
        if (result.aesKey && result.aesIv) {
          key = result.aesKey
          iv = result.aesIv
        }
      }
      if (res && key && iv && res.data) {
        const data = cryptoJSAesDecrypt(res.data, key, iv)
        if (data) {
          res.data = JSON.parse(data)
        }
      }
    }
    const isSuccessResponse = res.code === 1 || res.code === 10000 || res.code === 21605
    logApiResponse(response, res, isSuccessResponse ? 'log' : 'warn')
    // if the custom code is not 20000, it is judged as an error.
    // Vuex => 404
    if (res.code === 21605) {
      return res
    }
    if (res.code !== 1 && res.code !== 10000) {
      return Promise.reject(res)
    } else {
      return res
    }
  },
  error => {
    logApiError(error)
    return Promise.reject(error)
  }
)

// serviceUrl: 服务路径
function request({ serviceUrl = '/wallet-app-api', ...params }) {
  params.url = path.join(serviceUrl, params.url)
  return service(params)
}

export default request
